/**
 * MUI theme factory for tmm-product-admin. White-label-friendly — palette
 * tokens are the extension point if per-tenant branding is ever introduced.
 *
 * The theme is the single source of visual values (TMMPDM-0033, UI standard
 * TYPE-004/012, DS-005): feature code picks a variant, never a literal size.
 *
 * | Variant     | Size / line height        | Weight | Use                                        |
 * |-------------|---------------------------|--------|--------------------------------------------|
 * | h1          | 1.75rem (28px) / 36px     | 600    | page heading                               |
 * | h2          | 1.125rem (18px) / 24px    | 600    | section heading                            |
 * | h3          | 1rem (16px) / 22px        | 600    | sub-section / dialog title                 |
 * | body1, body2| 0.875rem (14px) / 20px    | 400    | working and reading text (TMM-T2 14/20)    |
 * | subtitle2   | 0.875rem / 20px           | 600    | emphasized values, selected nav            |
 * | caption     | 0.75rem (12px) / 16px     | 400    | metadata, helper text, chips               |
 * | overline    | 0.75rem / 16px, 0.06em    | 600    | rare uppercase labels only                 |
 * | kpi         | 1.5rem (24px) / 30px      | 600    | a headline metric (`KpiCard`), tabular     |
 * | button      | 0.875rem / 20px, no caps  | 500    | buttons                                    |
 * | numeric     | tabular-nums (no size)    | —      | every compared number (`Amount`, cells)    |
 * | mono        | JetBrains Mono (no size)  | —      | identifiers, slugs, references, codes      |
 *
 * Typeface (TMM-T1): Inter, bundled with @fontsource (`main.tsx`), with the `cv05` (tailed l) and
 * `cv08` (serifed I) variants on so I / l / 1 differ (TYPE-007); JetBrains Mono for the mono token.
 * The system stack stays as the fallback.
 *
 * Component defaults: input text and labels 14px (a shrunk label stays at 12px — TYPE-002),
 * helper text caption, table body body2 / head 13px 600 (row heights unchanged), list item
 * primary body2, tooltip caption, chip label caption 500. No weight 300 anywhere (TYPE-008).
 *
 * Colour (TMM-T4): `LIGHT_ROLES` / `DARK_ROLES` are the token-role vocabulary shared with tmm-app;
 * MUI's palette keys (`text.primary`, `primary.main`, `background.paper`, …) are aliases of them, and
 * the roles are emitted as `--mui-palette-roles-*` CSS variables. Feature code names a role or a
 * palette key, never a literal colour (ESLint `no-restricted-syntax` outside `theme/`), and never fades
 * content with opacity (`noOpacity.guard.test.ts`). The console accent is green (TMM-T5): `brand*`
 * are the `primary` tokens.
 *
 * Density (DEN-004/006/007): `spacing: 8` is the scale; `custom.density` names the row and
 * control heights. Tables are `size="small"` ≈ compact (~33px rows) and keep that look.
 */
import { createTheme, type Palette, type Theme } from '@mui/material/styles';

/** Inter first (bundled, `main.tsx`), the system stack as the fallback (TMM-T1). */
const SANS_FONT_FAMILY = [
  '"Inter"',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
].join(',');

/** JetBrains Mono (bundled), then the system monospace faces. */
const MONO_FONT_FAMILY = [
  '"JetBrains Mono"',
  'ui-monospace',
  'SFMono-Regular',
  'Menlo',
  'Consolas',
  'monospace',
].join(', ');

/** Inter's tailed `l` and serifed `I`, so I / l / 1 differ (TYPE-007). */
const FONT_FEATURE_SETTINGS = '"cv05", "cv08"';

const typeScale = {
  h1: { fontSize: '1.75rem', lineHeight: '2.25rem', fontWeight: 600 },
  h2: { fontSize: '1.125rem', lineHeight: '1.5rem', fontWeight: 600 },
  h3: { fontSize: '1rem', lineHeight: '1.375rem', fontWeight: 600 },
  body1: { fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 400 },
  body2: { fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 400 },
  subtitle2: { fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 600 },
  caption: { fontSize: '0.75rem', lineHeight: '1rem', fontWeight: 400 },
  overline: {
    fontSize: '0.75rem',
    lineHeight: '1rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
  },
  button: {
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    fontWeight: 500,
    textTransform: 'none',
  },
  kpi: {
    fontSize: '1.5rem',
    lineHeight: '1.875rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  },
  numeric: { fontVariantNumeric: 'tabular-nums' },
  mono: { fontFamily: MONO_FONT_FAMILY },
} as const;

/**
 * Room a sticky-header table leaves in the viewport (TMMPDM-0035, TBL-017): the 64px app bar, the
 * sticky page header (~125px with its title, subtitle, scope and meta lines) and, below the table,
 * the pager and the page's bottom padding (~80px), with a margin. A long table scrolls inside
 * `calc(100vh - offset)`, so even with the page scrolled to its end the table's own header row
 * sits below the page header and never slides under it.
 */
const STICKY_TABLE_OFFSET = 300;

/** A shrunk (floating) label renders at 12px: 14px × 0.8571. The outline's notch legend uses
 * the same ratio so the gap in the border matches the label. */
const SHRUNK_LABEL_SCALE = 0.8571;

/**
 * Light scheme — the tmm brand (Operator-approved 2026-09-18, contrast-checked before the spec
 * shipped). primary.main is used as text and as the Avatar's contrastText-on-primary background,
 * so it must clear the WCAG AA 4.5:1 floor the e2e axe scan (TMMPDM-0003) enforces:
 *   #065f46 vs #ffffff = 7.68:1, vs background.default #f7f8fa = 7.23:1
 *   #a16207 vs #ffffff = 4.92:1, vs background.default #f7f8fa = 4.63:1
 * Re-measure before changing either token; the source console's first primary failed this floor
 * at 3.5:1 and was caught by axe in the gate, not in review. Unchanged by TMMPDM-0039.
 */
export const LIGHT_PALETTE = {
  primary: { main: '#065f46' },
  secondary: { main: '#a16207' },
  success: { main: '#15803d' },
  warning: { main: '#b45309' },
  error: { main: '#b91c1c' },
  info: { main: '#0369a1' },
  background: { default: '#f7f8fa', paper: '#ffffff' },
} as const;

/**
 * Dark scheme (TMMPDM-0039, Operator-approved 2026-09-27). Every token measured (WCAG relative
 * luminance) against both surfaces — text ≥ 4.5:1, UI boundaries ≥ 3:1 (A11Y-004, TYPE-010):
 *
 * | Token                     | Value                   | vs default #0f1419 | vs paper #161b22 |
 * |---------------------------|-------------------------|--------------------|------------------|
 * | text.primary              | #e6edf3                 | 15.67:1            | 14.64:1          |
 * | text.secondary            | #9da7b3                 | 7.59:1             | 7.09:1           |
 * | primary.main              | #34d399                 | 9.63:1             | 9.00:1           |
 * | primary.contrastText      | #0f1419 on #34d399      | 9.63:1             | —                |
 * | secondary.main            | #fbbf24                 | 11.09:1            | 10.36:1          |
 * | success.main              | #4ade80                 | 10.62:1            | 9.93:1           |
 * | warning.main              | #fbbf24                 | 11.09:1            | 10.36:1          |
 * | error.main                | #f87171                 | 6.69:1             | 6.25:1           |
 * | info.main                 | #38bdf8                 | 8.64:1             | 8.07:1           |
 * | input outline (boundary)  | #6e7681                 | 4.03:1             | 3.77:1           |
 * | divider (decorative)      | rgba(230,237,243,0.12)  | 1.48:1             | 1.38:1           |
 *
 * The divider only separates regions (like the light scheme's 12% black) and carries no meaning,
 * so the 3:1 non-text floor does not apply to it; an input's outline does, hence `DARK_OUTLINE`
 * (the dark `lineStrong` role; the light outline is the light `lineStrong`, TMMPDM-0064).
 * The top bar takes the paper surface rather than MUI's neutral grey 900. `theme.test.ts`
 * recomputes these ratios from the tokens, so a changed token that drops below a floor fails.
 */
export const DARK_PALETTE = {
  primary: { main: '#34d399', contrastText: '#0f1419' },
  secondary: { main: '#fbbf24' },
  success: { main: '#4ade80' },
  warning: { main: '#fbbf24' },
  error: { main: '#f87171' },
  info: { main: '#38bdf8' },
  background: { default: '#0f1419', paper: '#161b22' },
  text: { primary: '#e6edf3', secondary: '#9da7b3' },
  divider: 'rgba(230,237,243,0.12)',
  AppBar: { defaultBg: '#161b22' },
} as const;

/** An outlined input's resting border in the dark scheme — MUI's 23% white is 2.1:1 on paper. */
export const DARK_OUTLINE = '#6e7681';

/**
 * The token-role vocabulary (TMM-T4, TMMPDM-0064), light scheme. Every role that has a measured
 * token above is that token under a role name; the rest are new, measured by `contrast.audit.test.ts`
 * (every text role against every surface it sits on, and `Ink` on `Fill`, ≥ 4.5:1) and not used by a
 * component until a spec asks:
 *
 * | Role                | Value                       | Source                                       |
 * |---------------------|-----------------------------|----------------------------------------------|
 * | bg / surface        | #f7f8fa / #ffffff           | background.default / paper (measured)        |
 * | surface2            | #f2f4f7                     | new: a quieter band inside a surface         |
 * | surfaceRaised       | #ffffff                     | new: menus, dialogs, popovers                |
 * | text / text2        | #212121 / #666666           | MUI's 87% / 60% black, as the opaque result  |
 * | text3               | #6b6b6b                     | new: quietest text that still clears AA      |
 * | link, brand*        | the `primary` tokens        | green accent (TMM-T5, Operator 2026-09-18)   |
 * | line                | rgba(0,0,0,0.12)            | MUI's divider (decorative)                   |
 * | lineStrong          | #767676                     | new: a ≥ 3:1 boundary (A11Y-004)             |
 * | {family}Fg/Text/Fill| the family's `main`         | success/warning/error/info (measured)       |
 * | {family}Ink         | #ffffff                     | text on the family's Fill                    |
 * | {family}Soft        | a pale tint                 | new: alert and pill backgrounds              |
 * | money*              | success / error Text        | positive / negative amounts                  |
 * | scrim               | rgba(0,0,0,0.5)             | MUI's backdrop                               |
 */
export const LIGHT_ROLES: Palette['roles'] = {
  bg: LIGHT_PALETTE.background.default,
  surface: LIGHT_PALETTE.background.paper,
  surface2: '#f2f4f7',
  surfaceRaised: '#ffffff',
  text: '#212121',
  text2: '#666666',
  text3: '#6b6b6b',
  link: LIGHT_PALETTE.primary.main,
  line: 'rgba(0, 0, 0, 0.12)',
  lineStrong: '#767676',
  focusRing: LIGHT_PALETTE.primary.main,
  brandFg: LIGHT_PALETTE.primary.main,
  brandText: LIGHT_PALETTE.primary.main,
  brandFill: LIGHT_PALETTE.primary.main,
  brandInk: '#ffffff',
  brandSoft: '#e6f4ef',
  brandBoundary: LIGHT_PALETTE.primary.main,
  successFg: LIGHT_PALETTE.success.main,
  successText: LIGHT_PALETTE.success.main,
  successFill: LIGHT_PALETTE.success.main,
  successInk: '#ffffff',
  successSoft: '#ecf7f0',
  warningFg: LIGHT_PALETTE.warning.main,
  warningText: LIGHT_PALETTE.warning.main,
  warningFill: LIGHT_PALETTE.warning.main,
  warningInk: '#ffffff',
  warningSoft: '#fdf3e3',
  errorFg: LIGHT_PALETTE.error.main,
  errorText: LIGHT_PALETTE.error.main,
  errorFill: LIGHT_PALETTE.error.main,
  errorInk: '#ffffff',
  errorSoft: '#fdecec',
  infoFg: LIGHT_PALETTE.info.main,
  infoText: LIGHT_PALETTE.info.main,
  infoFill: LIGHT_PALETTE.info.main,
  infoInk: '#ffffff',
  infoSoft: '#e6f1f8',
  moneyPositive: LIGHT_PALETTE.success.main,
  moneyNegative: LIGHT_PALETTE.error.main,
  scrim: 'rgba(0, 0, 0, 0.5)',
};

/** The dark scheme's roles: the measured tokens in the table above, plus new measured surfaces. */
export const DARK_ROLES: Palette['roles'] = {
  bg: DARK_PALETTE.background.default,
  surface: DARK_PALETTE.background.paper,
  surface2: '#1c2330',
  surfaceRaised: '#1f2630',
  text: DARK_PALETTE.text.primary,
  text2: DARK_PALETTE.text.secondary,
  text3: '#8b96a3',
  link: DARK_PALETTE.primary.main,
  line: DARK_PALETTE.divider,
  lineStrong: DARK_OUTLINE,
  focusRing: DARK_PALETTE.primary.main,
  brandFg: DARK_PALETTE.primary.main,
  brandText: DARK_PALETTE.primary.main,
  brandFill: DARK_PALETTE.primary.main,
  brandInk: DARK_PALETTE.primary.contrastText,
  brandSoft: '#0f2a22',
  brandBoundary: DARK_PALETTE.primary.main,
  successFg: DARK_PALETTE.success.main,
  successText: DARK_PALETTE.success.main,
  successFill: DARK_PALETTE.success.main,
  successInk: DARK_PALETTE.primary.contrastText,
  successSoft: '#12291a',
  warningFg: DARK_PALETTE.warning.main,
  warningText: DARK_PALETTE.warning.main,
  warningFill: DARK_PALETTE.warning.main,
  warningInk: DARK_PALETTE.primary.contrastText,
  warningSoft: '#2d2410',
  errorFg: DARK_PALETTE.error.main,
  errorText: DARK_PALETTE.error.main,
  errorFill: DARK_PALETTE.error.main,
  errorInk: DARK_PALETTE.primary.contrastText,
  errorSoft: '#2f1818',
  infoFg: DARK_PALETTE.info.main,
  infoText: DARK_PALETTE.info.main,
  infoFill: DARK_PALETTE.info.main,
  infoInk: DARK_PALETTE.primary.contrastText,
  infoSoft: '#0e2536',
  moneyPositive: DARK_PALETTE.success.main,
  moneyNegative: DARK_PALETTE.error.main,
  scrim: 'rgba(0, 0, 0, 0.5)',
};

/**
 * A scheme's MUI palette: the measured tokens with the roles as their source for text, and the
 * roles themselves (`palette.roles`) emitted as CSS variables. `Ink` is each colour's `contrastText`.
 */
function schemePalette(base: typeof LIGHT_PALETTE | typeof DARK_PALETTE, roles: Palette['roles']) {
  return {
    ...base,
    primary: { main: base.primary.main, contrastText: roles.brandInk },
    success: { main: base.success.main, contrastText: roles.successInk },
    warning: { main: base.warning.main, contrastText: roles.warningInk },
    error: { main: base.error.main, contrastText: roles.errorInk },
    info: { main: base.info.main, contrastText: roles.infoInk },
    text: { primary: roles.text, secondary: roles.text2 },
    roles,
  };
}

export function createAppTheme(): Theme {
  return createTheme({
    // One theme object for both schemes; the class on <html> picks one (`.light` / `.dark`).
    cssVariables: { colorSchemeSelector: 'class' },
    colorSchemes: {
      light: { palette: schemePalette(LIGHT_PALETTE, LIGHT_ROLES) },
      dark: { palette: schemePalette(DARK_PALETTE, DARK_ROLES) },
    },
    spacing: 8,
    typography: {
      // Inter, bundled (TMM-T1, TMMPDM-0064); the system stack TMMPDM-0033 used stays as the fallback.
      fontFamily: SANS_FONT_FAMILY,
      fontWeightLight: 400,
      allVariants: { fontFeatureSettings: FONT_FEATURE_SETTINGS },
      ...typeScale,
    },
    shape: { borderRadius: 8 },
    custom: {
      density: { rowHeight: { compact: 32, standard: 40 }, controlHeight: 36 },
      layout: { stickyTableOffset: STICKY_TABLE_OFFSET },
    },
    components: {
      MuiCssBaseline: {
        // Inherited by everything on the page, controls included (TMM-T1).
        // A heading that route focus lands on (TMM-B7) takes focus without a ring.
        styleOverrides: {
          body: { fontFeatureSettings: FONT_FEATURE_SETTINGS },
          'h1[tabindex="-1"]:focus': { outline: 'none' },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: { fontSize: typeScale.body2.fontSize },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: ({ theme }) => ({
            '& legend': { fontSize: `${SHRUNK_LABEL_SCALE}em` },
            // The `lineStrong` role in both schemes (≥ 3:1, A11Y-004); hover and focus borders
            // keep MUI's higher-specificity rules. `vars` resolves per scheme.
            borderColor: (theme.vars ?? theme).palette.roles.lineStrong,
          }),
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { fontSize: typeScale.body2.fontSize },
          outlined: {
            '&.MuiInputLabel-shrink': {
              transform: `translate(14px, -9px) scale(${SHRUNK_LABEL_SCALE})`,
            },
          },
          standard: {
            '&.MuiInputLabel-shrink': {
              transform: `translate(0, -1.5px) scale(${SHRUNK_LABEL_SCALE})`,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: { ...typeScale.caption },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          body: { fontSize: typeScale.body2.fontSize },
          head: { fontSize: '0.8125rem', fontWeight: 600 },
          alignRight: { ...typeScale.numeric },
          // A sticky header keeps the table's look: paper, not the page background.
          // `vars` resolves per scheme; `theme.palette` is the light scheme only.
          stickyHeader: ({ theme }) => ({
            backgroundColor: (theme.vars ?? theme).palette.background.paper,
          }),
        },
      },
      MuiIconButton: {
        styleOverrides: {
          // A small icon button is a 32px target (A11Y-008) around its icon.
          sizeSmall: { padding: 6, minWidth: 32, minHeight: 32 },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: { fontSize: typeScale.body2.fontSize, lineHeight: typeScale.body2.lineHeight },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { ...typeScale.caption },
        },
      },
      MuiChip: {
        styleOverrides: {
          label: { fontSize: typeScale.caption.fontSize, fontWeight: 500 },
        },
      },
      // TMMPDM-0085 style B: one treatment for every tab. Wash and text come from palette
      // tokens. A 1px border is always reserved; hover and selected paint it from primary.main.
      MuiTabs: {
        styleOverrides: {
          indicator: ({ theme }) => ({
            backgroundColor: (theme.vars ?? theme).palette.primary.main,
          }),
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => {
            const scheme = theme.vars ?? theme;
            const wash = `color-mix(in srgb, ${scheme.palette.primary.main} 9%, transparent)`;
            const radius = `${theme.shape.borderRadius}px`;
            const edge = scheme.palette.primary.main;
            return [
              {
                fontWeight: 400,
                borderTopLeftRadius: radius,
                borderTopRightRadius: radius,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'transparent',
                '&:hover': {
                  backgroundColor: wash,
                  borderColor: edge,
                  color: scheme.palette.primary.dark,
                },
                '&.Mui-selected': {
                  backgroundColor: wash,
                  borderColor: edge,
                  color: scheme.palette.primary.dark,
                  fontWeight: 500,
                },
                '&.Mui-focusVisible': {
                  outlineWidth: 2,
                  outlineStyle: 'solid',
                  outlineColor: edge,
                  outlineOffset: 2,
                },
              },
              theme.applyStyles('dark', {
                '&:hover': {
                  backgroundColor: wash,
                  borderColor: edge,
                  color: scheme.palette.primary.light,
                },
                '&.Mui-selected': {
                  backgroundColor: wash,
                  borderColor: edge,
                  color: scheme.palette.primary.light,
                  fontWeight: 500,
                },
              }),
            ];
          },
        },
      },
    },
  });
}
